import React, { useState, useRef } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

export default function UpdateBioSection({
  bio,
  setBio,
  onSubmit,
  isUpdatingBio,
  hasSubscriptionAccess = true,
  hasUsedFreeTrial = false,
}) {
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const dismissedRef = useRef(false);
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  const openSubscription = usePremiumUpsellRedirect(user);

  const handleBlockedBioAction = (event) => {
    if (dismissedRef.current) return;
    event?.preventDefault();
    event?.target?.blur();
    setOpenPremiumFeatureModal(true);
  };

  const handleSubmit = (event) => {
    if (!hasSubscriptionAccess) {
      handleBlockedBioAction(event);
      return;
    }

    onSubmit(event);
  };

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  return (
    <form className="w-full mt-4" onSubmit={handleSubmit}>
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => {
          dismissedRef.current = true;
          setOpenPremiumFeatureModal(false);
          setTimeout(() => {
            dismissedRef.current = false;
          }, 300);
        }}
        title="Premium Feature"
        description="Bio updates are locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="bg-white shadow-lg p-4 rounded-lg">
        <h1 className="text-xl mb-6 uppercase">UPDATE BIO</h1>
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Write your bio here..."
          value={bio}
          onClick={!hasSubscriptionAccess ? handleBlockedBioAction : undefined}
          onFocus={!hasSubscriptionAccess ? handleBlockedBioAction : undefined}
          onChange={(e) => {
            if (hasSubscriptionAccess) {
              setBio(e.target.value);
            }
          }}
          readOnly={!hasSubscriptionAccess}
          style={{
            maxHeight: "400px",
            overflow: "auto",
            resize: "vertical",
          }}
        />
        <div className="flex justify-center mt-4">
          <button
            type={hasSubscriptionAccess ? "submit" : "button"}
            onClick={
              !hasSubscriptionAccess ? handleBlockedBioAction : undefined
            }
            className={`mt-2 px-4 py-2 rounded-md ${
              isUpdatingBio ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-800"
            } text-white`}
            disabled={hasSubscriptionAccess && isUpdatingBio}
          >
            {isUpdatingBio ? "Updating..." : "UPDATE  BIO"}
          </button>
        </div>
      </div>
    </form>
  );
}
